import React, { useCallback, useEffect, useRef, useState } from 'react';
// import "./multiRangeSlider.css";

interface RangeValue {
  min: number;
  max: number;
}

interface MultiRangeSliderProps {
  min: number;
  max: number;
  onChange: (event: RangeValue) => void;
}

export const MultiRangeSlider = ({
  min,
  max,
  onChange,
}: MultiRangeSliderProps) => {
  const [minVal, setMinVal] = useState(min);
  const [maxVal, setMaxVal] = useState(max);
  const minValRef = useRef<HTMLInputElement>(null);
  const maxValRef = useRef<HTMLInputElement>(null);
  const range = useRef<HTMLDivElement>(null);

  // Convert to percentage
  const getPercent = useCallback(
    (value: number) => Math.round(((value - min) / (max - min)) * 100),
    [min, max]
  );

  // Set width of the range to decrease from the left side
  useEffect(() => {
    if (maxValRef.current) {
      const minPercent = getPercent(minVal);
      const maxPercent = getPercent(maxVal);

      if (range.current) {
        range.current.style.left = `${minPercent}%`;
        range.current.style.width = `${maxPercent - minPercent}%`;
      }
    }
  }, [minVal, maxVal, getPercent]);

  // Set width of the range to decrease from the right side
  useEffect(() => {
    if (minValRef.current) {
      const minPercent = getPercent(minVal);
      const maxPercent = getPercent(maxVal);

      if (range.current) {
        range.current.style.width = `${maxPercent - minPercent}%`;
      }
    }
  }, [minVal, maxVal, getPercent]);

  // Get min and max values when their state changes
  useEffect(() => {
    onChange({ min: minVal, max: maxVal });
  }, [minVal, maxVal, onChange]);

  return (
    <div className="h-screen flex align-middle justify-center">
      <input
        type="range"
        min={min}
        max={max}
        value={minVal}
        ref={minValRef}
        onChange={(event) => {
          const value = Math.min(+event.target.value, maxVal - 1);
          setMinVal(value);
          event.target.value = value.toString();
        }}
        className={`pointer-events-none absolute h-0 w-200 outline-none z-30 ${
          minVal > max - 100 ? 'z-50' : ''
        }`}
      />
      <input
        type="range"
        min={min}
        max={max}
        value={maxVal}
        ref={maxValRef}
        onChange={(event) => {
          const value = Math.max(+event.target.value, minVal + 1);
          setMaxVal(value);
          event.target.value = value.toString();
        }}
        className="pointer-events-none absolute h-0 w-200 outline-none z-40"
      />

      <div className="relative w-48">
        <div className="absolute rounded-sm bg-blue-700 w-full z-10" />
        <div ref={range} className="absolute rounded-sm bg-blue-900 z-20" />
        <div className="absolute mt-6 left-2">{minVal}</div>
        <div className="absolute mt-6 right-1">{maxVal}</div>
      </div>
    </div>
  );
};

export default MultiRangeSlider;
